import React, { useContext, useRef, useEffect, useState } from 'react';
import proptypes from 'prop-types';
const { string, shape, bool, object } = proptypes;
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { ImpressionProvider, useImpression } from '@thd-olt-component-react/impression';

import {
  params,
  extend,
  shape as dsShape,
  string as dsString,
  QueryProvider,
  number as dsNumber,
  arrayOf as dsArrayOf,
  bool as dsBool
} from '@thd-nucleus/data-sources';
import { Carousel } from '@one-thd/sui-carousel';

import {
  getSchema,
  getSchemaId,
  toUpperCaseFirstLetter
} from '../../utils/helpers';
import { triggerNewRelic, triggerNewRelicTimeLog } from '../../utils/newRelicHelpers';
import { buildRevJetBanner } from '../../utils/revjet';
import useSponsoredBannerApi from '../../hooks/useSponsoredBannerApi';
import { DefaultBanner } from '../DefaultBanner/DefaultBannerComponent';
import {
  SponsoredUniversalBannerCreative
} from '../SponsoredUniversalBannerCreative/SponsoredUniversalBannerCreative';
import { initImpressionWatchers } from '../../utils/initImpressionWatchers';
import { newRelicConstants } from '../../constants';
import { adServerClients, SUPPRESSED_NVALUES } from '../../constants/constants';
import { shouldSuppressBanner } from "../../utils/suppressionHelper";

const SponsoredUniversalBannerComponent = ({
  contentfulDefaultBanner,
  pageContext,
  schema,
  bannerType,
  renderDefaultBanner,
  renderHarmony,
  nValue
}) => {
  const experienceContext = useContext(ExperienceContext);
  let { guestUserID, mcvisID } = useThdCustomer();

  const suppressedNValues = useConfigService(SUPPRESSED_NVALUES);
  const isLeaderboardEnabled = useConfigService('fs-prop:isLeaderboardEnabled') || false;

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (experienceContext?.mounted && !mounted) {
      setMounted(true);
    }
  }, [experienceContext?.mounted, mounted]);

  let matchProducts;
  if (renderHarmony) {
    mcvisID = '1234';
    guestUserID = '4321';
    matchProducts = [1.1];

  }

  const bannerSchema = getSchema({ schema, pageContext });
  const schemaId = getSchemaId(bannerSchema, bannerType);
  const shouldSuppress = shouldSuppressBanner({
    bannerType, experienceContext, pageContext, suppressedNValues
  });
  const [hasTriggeredNewRelicForSuppression, setHasTriggeredNewRelicForSuppression] = useState(false);
  const newRelicBannerType = newRelicConstants[`${bannerType.toUpperCase()}_BANNER`];
  const bannerReceivedRef = useRef(null);
  const apiRequestTimestamp = new Date().getTime();
  const noAdReturnedTriggeredRef = useRef(false);
  const anchorSku = pageContext?.data?.itemId;

  useEffect(() => {
    if (shouldSuppress && !hasTriggeredNewRelicForSuppression){
        triggerNewRelic(newRelicBannerType, 'banner-suppressed');
        setHasTriggeredNewRelicForSuppression(true);
      }
  }, [shouldSuppress, hasTriggeredNewRelicForSuppression]);

  let { data, loading, error } = useSponsoredBannerApi({
    bannerType,
    guestUserID,
    experienceContext,
    pageContext,
    schema: bannerSchema,
    mcvisID,
    shouldSuppress,
    isLeaderboardEnabled,
    matchProducts,
    anchorSku,
    mounted,
    nValue
  });

  let apiResponseTimestamp;
  const showBanner = !!(!loading && data?.sponsored?.banner);

  if (showBanner && bannerReceivedRef.current !== false) {
    apiResponseTimestamp = new Date().getTime();
    bannerReceivedRef.current = !bannerReceivedRef.current;
  }

  let adWasNotReturned = !loading && !error && data?.data?.sponsored?.banner === null && !noAdReturnedTriggeredRef.current;
  if (error) {
    triggerNewRelic(newRelicBannerType, 'BANNER-API-FAILURE');
  }
  if (adWasNotReturned) {
    triggerNewRelic(newRelicBannerType, 'BANNER-API-204');
    noAdReturnedTriggeredRef.current = true;
  }

  if (bannerReceivedRef.current) {
    if (bannerType === 'standard') {
      initImpressionWatchers(['UNIVERSAL_STANDARD_BANNER']);
    } else {
      initImpressionWatchers();
    }
    triggerNewRelic(newRelicBannerType, 'BANNER-API-200');
    const sponsoredData = data.sponsored;
    const requestConfig = {
      schemaId,
      experienceContext,
      pageContext,
      bannerType,
      mcvisID,
    };
    requestConfig.startTime = apiRequestTimestamp;
    buildRevJetBanner({
      data: sponsoredData,
      requestConfig,
      useKevelInnervate: false
    });
    // SEND TIMESTAMPS TO CALCULATE API RESPONSE TIME
    triggerNewRelicTimeLog(newRelicConstants[`${bannerType.toUpperCase()}_BANNER_API_RESPONSE`], apiRequestTimestamp, apiResponseTimestamp, adServerClients.KEVEL);
    // SEND TIME FROM BANNER INITIATED TO BANNER WINNER RECEIVED
    triggerNewRelicTimeLog(newRelicConstants[`${bannerType.toUpperCase()}_BANNER_AD_INIT_TO_WINNER`], apiRequestTimestamp, apiResponseTimestamp, adServerClients.KEVEL);
  }

  return (
    <div
      data-component={`Sponsored${bannerType}Banner`}
      className={`sponsored_${bannerType}_banner_container sui-overflow-hidden`}
    >

      {renderDefaultBanner && showBanner && (
        <Carousel
          containerId="sponsored-standard-banner"
          type="peekless"
          layout="full-bleed"
          slidesPerView={1}
          slidesPerGroup={2}
          disableMargin
          disableShadow
          SwiperProps={
            { autoplay: { delay: 500, stopOnLastSlide: true, } }
          }
        >
          <ImpressionProvider
            data={{
              id: '',
              name: 'SponsoredUniversalBanner',
              component: 'SponsoredUniversalBanner',
              position: 1,
              type: 'sponsoredcontent',
            }}
          >

            <DefaultBanner
              {...{
                experienceContext,
                contentfulDefaultBanner,
                schema
              }}
            />
          </ImpressionProvider>
          <ImpressionProvider
            data={{
              id: '',
              name: 'SponsoredUniversalBanner',
              component: 'SponsoredUniversalBanner',
              type: 'sponsoredcontent',
            }}
          >

            <div className="sui-w-full sui-h-auto">
              <SponsoredUniversalBannerCreative
                {...{ schemaId,
                  showBanner,
                  bannerType,
                  data }}
              />
            </div>

          </ImpressionProvider>

        </Carousel>
      )}

      {renderDefaultBanner && !showBanner && (
        <ImpressionProvider
          data={{
            id: '',
            name: 'SponsoredUniversalBanner',
            component: 'SponsoredUniversalBanner',
            position: 1,
            type: 'sponsoredcontent',
          }}
        >
          <DefaultBanner
            {...{
              experienceContext,
              contentfulDefaultBanner,
              schema: bannerSchema
            }}
          />

        </ImpressionProvider>

      )}

      {!renderDefaultBanner && showBanner && (
        <ImpressionProvider
          data={{
            id: '',
            name: 'SponsoredUniversalBanner',
            component: 'SponsoredUniversalBanner',
            type: 'sponsoredcontent',
          }}
        >
          <SponsoredUniversalBannerCreative
            {...{ schemaId,
              showBanner,
              bannerType,
              data }}
          />
        </ImpressionProvider>

      )}

    </div>
  );

};

const propTypes = {
  contentfulDefaultBanner: object,
  pageContext: shape({
    label: string
  }),
  schema: string,
  nValue: string,
  bannerType: string,
  renderDefaultBanner: bool,
  renderHarmony: bool
};

const defaultProps = {
  contentfulDefaultBanner: {},
  pageContext: {},
  schema: '',
  bannerType: '',
  renderDefaultBanner: false,
  renderHarmony: false,
  nValue: ''
};

SponsoredUniversalBannerComponent.propTypes = propTypes;
SponsoredUniversalBannerComponent.defaultProps = defaultProps;
SponsoredUniversalBannerComponent.dataModel = extend({
  sponsored: params({
    bannerType: dsString(),
    // matchProducts: dsArrayOf(dsNumber({ float: true })),
    matchProducts: dsArrayOf(dsNumber({ float: true })),
    keyword: dsString(),
    marketingId: dsString().isRequired(),
    pageType: dsString().isRequired(),
    customerType: dsString(),
    category: dsString(),
    channel: dsString().isRequired(),
    customerIdentifier: dsString(),
    anchorSku: dsNumber({ float: true }),
    nValue: dsString(),
    forceAd: dsBool()
  }).shape({
    banner: dsShape({
      bannerURL: dsString(),
      clickthruURL: dsString(),
      onClickBeacon: dsString(),
      clickConversionBeacon: dsString(),
      viewConversionBeacon: dsString(),
      onViewBeacon: dsString(),
      slotId: dsString(),
      adRequestId: dsString(),
      campaignId: dsString(),
      placementId: dsString(),
      flightId: dsString(),
      innervateKey: dsString(),
      innervateTag: dsString()
    })
  })
});

const SponsoredUniversalBanner = (props) => {

  const skipFn = ({ skip, queryName }) => {
    return skip;
  };
  return (
    <QueryProvider cacheKey="sponsored-universal-banner" skip={skipFn}>
      <SponsoredUniversalBannerComponent {...props} />
    </QueryProvider>
  );
};
SponsoredUniversalBanner.propTypes = propTypes;

SponsoredUniversalBanner.defaultProps = defaultProps;
SponsoredUniversalBanner.dataModel = extend({}, SponsoredUniversalBannerComponent);
export { SponsoredUniversalBanner };
